<template>
	<div id="holder">
		<iframe v-if="srcDoc != ''" id="ar-iframe" :srcdoc="srcDoc"></iframe>
		<iframe v-if="srcUrl != ''" id="ar-iframe" :src="srcUrl"
			allow="fullscreen; camera; microphone; gyroscope; accelerometer; xr-spatial-tracking"
			allowfullscreen></iframe>
		<ModalComponent :show="showClaimModal" @close="showClaimModal = false" @claim="claimItem()" />
		<LoadingComponent v-if="loading" />
	</div>
</template>

<script lang="ts">
declare global {
	interface Window {
		claimedIds?: Set<string>;
		claimInFlightIds?: Set<string>;
	}
}

import ModalComponent from "@/components/utils/ModalComponent.vue";
import LoadingComponent from "@/components/utils/LoadingComponent.vue";
import { getUserLocation } from "@/utils/geolocationTools";
import { Action, ActionType, Container } from "@/types/customPage";
import { ARTemplate } from "@/types/arTemplate";
import { DropLocation, DropPool } from "@/types/dropPool";
import { defineComponent, PropType } from "vue";
import { navigationStore } from "@/store/pinia_modules/navigationStore";
import { dropPoolStore } from "@/store/pinia_modules/dropPoolStore";
import { experienceStore } from "@/store/pinia_modules/experienceStore";
import { vItemStore } from "@/store/pinia_modules/vItemStore";
import { ItemStatus } from "@/types/vItem";
import { authStore } from "@/store/pinia_modules/authStore";
import { poolSegmentStore } from "@/store/pinia_modules/poolSegmentStore";
import { campaignStore } from "@/store/pinia_modules/campaignStore";
import router from "@/router";
import { deepLinkStore } from "@/store/pinia_modules/deepLinkStore";

export default defineComponent({
	props: {
		element: {
			type: Object as PropType<Container>,
			required: true,
		},
	},
	components: { ModalComponent, LoadingComponent },
	data() {
		return {
			showClaimModal: false,
			selectedItemID: "",
			srcDoc: "",
			srcUrl: "",
			template: {} as ARTemplate,
			iframeElement: null as HTMLIFrameElement | null,
			loading: false,
			reachedAccuracy: false,
			initNoItems: false,
			rendered: false,
			templateHasAnimations: false,
			iframeMessageHandler: null as ((event: MessageEvent) => void) | null,
			locationTimeout: null as number | NodeJS.Timeout | null,
		};
	},

	async mounted() {
		this.loading = true;

		// Flush stale vItems so the iframe never receives already-claimed data.
		// The drop call below will fetch a fresh set from the backend.
		vItemStore().clearItems();

		await this.getPoolsCategoriesPrizes();
		experienceStore().resolveFromState({
			routeName: "AR",
			pools: dropPoolStore().getPools,
			pendingItems: vItemStore().getPendingItems,
		});

		// Refresh the active pool with the fresh data
		dropPoolStore().saveActiveDropPool({
			poolID: experienceStore().preferredPoolID,
			dropType: experienceStore().preferredDropType,
			fallbackDropType:
				experienceStore().preferredDropType === "gps" ? "pin" : "gps",
		});

		const id = dropPoolStore().getWatcher;
		if (id != -1) {
			navigator.geolocation.clearWatch(id);
			dropPoolStore().saveWatcher(-1);
		}
		navigationStore().isInARView();
		this.setupIframeListener();
		const shouldRequestLocation = await this.shouldRequestLocationPermission();
		if (shouldRequestLocation) {
			dropPoolStore().saveLocationAccuracy(1000000000);
			// Arm the backstop alongside the location request. getUserLocation()
			// only redirects on PERMISSION_DENIED; POSITION_UNAVAILABLE, TIMEOUT
			// and iOS's silent no-callback case all leave `loading` true forever
			// otherwise. Timer-based rather than error-code-based so it covers
			// every one of those without special-casing each.
			this.setGetLocationTimeout();
			this.initUserLocation();
		} else if (this.vItems.length > 0) {
			await this.checkForEnhancedAI();
		} else {
			this.reachedAccuracy = true;
			await this.init();
		}
	},
	beforeUnmount() {
		console.log("ARComponent beforeUnmount");
		if (this.locationTimeout) {
			clearTimeout(this.locationTimeout);
			this.locationTimeout = null;
		}
		if (this.iframeMessageHandler) {
			window.removeEventListener("message", this.iframeMessageHandler);
			this.iframeMessageHandler = null;
		}
	},
	computed: {
		vItems() {
			if (vItemStore().getPendingItems.length > 0) {
				return vItemStore().getPendingItems;
			}
			return vItemStore().getDroppedItems;
		},
		action(): Action {
			return navigationStore().getAction;
		},
		activePool(): DropPool | null {
			return dropPoolStore().getActivePool;
		},
		activeDropLocation(): DropLocation | null {
			return dropPoolStore().getActiveDropLocation;
		},
		// NOTE: `loading` is deliberately NOT a computed here. It is a local
		// data field (see data() above) because this component drives it
		// imperatively (`this.loading = true/false`) from mounted(),
		// checkForEnhancedAI() and claimItem(). A store-backed computed both
		// collided with the data field — which Vue resolves in favour of data,
		// silently dropping the computed and warning "[Vue warn]: Computed
		// property "loading" is already defined in Data." — and coupled AR's
		// spinner to MapComponent's independent loading flag.
		walletId(): string | null {
			return authStore().getWalletID;
		},
		userLocation(): [number, number] {
			return dropPoolStore().getUserLocation;
		},
		locationAccuracy(): number {
			return dropPoolStore().getLocationAccuracy;
		},
		requiresGpsLocation(): boolean {
			return experienceStore().requiresGpsLocation;
		},
	},
	watch: {
		action(to: Action): void {
			if (to) {
				if (to.type == "claim-item") {
					this.claimItem();
				}
			}
		},
		locationAccuracy(to: number) {
			if (to && to <= 100 && !this.reachedAccuracy) {
				// if (to && to <= 1000000 && !this.reachedAccuracy) {
				if (this.locationTimeout) {
					clearTimeout(this.locationTimeout);
					this.locationTimeout = null;
				}
				this.reachedAccuracy = true;
				this.init();
			}
		},
		userLocation(to: [number, number]) {
			if (to && this.requiresGpsLocation) {
				if (vItemStore().getPendingItems.length == 0) {
					dropPoolStore().saveActiveDropPool({
						poolID: experienceStore().preferredPoolID,
						dropType: experienceStore().preferredDropType,
						fallbackDropType: "pin",
					});
				}
			}
		},
	},
	methods: {
			async checkForEnhancedAI() {
				if (this.initNoItems && this.vItems.length == 0) {
					this.loading = false;
					return;
				}

				if (this.vItems.length > 0 && !this.rendered) {
					//drop items here if bypass map
					this.loading = true;
					const walletId = authStore().getWalletID;
					this.rendered = true;
					if (this.activePool?.arTemplateId) {
						this.template = await dropPoolStore().getARTemplate(
							this.activePool.arTemplateId
						);
						// getARTemplate resolves to undefined on any API failure
						// (both its catch branch and its !response.success branch
						// fall off the end). Without this guard the .framework
						// read below throws, aborting the method before it can
						// reach `this.loading = false` — a permanently stuck
						// spinner on a black screen.
						if (!this.template) {
							const campaign = campaignStore().getItem;
							console.error("No template found to render AR");
							this.loading = false;
							router.push(`/${campaign?.id}/vault`);
							return;
						}
						if (this.template.framework == "unity") {
							this.srcUrl = this.template.srcUrl! + "/template.html";
							this.loading = false;
							return;
						}
					} else {
						const campaign = campaignStore().getItem;
						if (campaign?.pendingarTemplateId) {
							this.template = await dropPoolStore().getARTemplate(
								campaign?.pendingarTemplateId || ""
							);
						} else {
							console.error("No template found to render AR");
							this.loading = false;
							router.push(`/${campaign?.id}/vault`);
							return;
						}
					}
					const campaign = campaignStore().getItem;

					this.srcDoc = await dropPoolStore().renderARTemplate({
						walletId: walletId || "",
						arTemplateID:
							(this.activePool?.useEnhancedAI
								? this.activePool.arTemplateId
								: campaign?.pendingarTemplateId || "legacy") || "legacy",
						dropPoolId: this.activePool?.id || "",
						deepLinkId: deepLinkStore().deepLink?.id || "",
						userLocation: this.requiresGpsLocation ? this.userLocation : undefined,
					});
					this.loading = false;
				} else if (this.vItems.length == 0) {
					//get user location
					this.loading = true;
					this.initNoItems = true;
					if (await this.shouldRequestLocationPermission()) {
						this.initUserLocation();
					} else {
						this.loading = false;
					}
				}
			},
			async init() {
				// if pending item, don't drop items
				if (vItemStore().getPendingItems.length == 0) {
					vItemStore().clearItems();
					dropPoolStore().saveActiveDropPool({
						poolID: experienceStore().preferredPoolID,
						dropType: experienceStore().preferredDropType,
						fallbackDropType: "pin",
					});
					await this.dropItems();
				}
				this.checkForEnhancedAI();
			},
			initUserLocation() {
				getUserLocation();
			},
			async shouldRequestLocationPermission(): Promise<boolean> {
				return this.requiresGpsLocation;
			},
			setGetLocationTimeout() {
				if (this.locationTimeout) {
					clearTimeout(this.locationTimeout);
				}
				this.locationTimeout = setTimeout(() => {
					this.locationTimeout = null;
					if (!this.reachedAccuracy) {
						navigationStore().emitAction({
							type: ActionType.OPEN_POPUP,
							value: "location-error",
						});
						this.loading = false;
					}
				}, 10000);
			},
			async getPoolsCategoriesPrizes() {
				await dropPoolStore().getMapData();
			},
			async getCampaignSegments() {
				poolSegmentStore().getCampaignSegments();
			},
		async dropItems() {
			// if pending item, don't drop items
			if (vItemStore().getPendingItems.length > 0) {
				return false;
			}

			// Ensure we have an active pool to check against.
			// This determines if there's a campaign running at the user's location and time.
			if (!this.activePool) {
				dropPoolStore().saveActiveDropPool({
					poolID: experienceStore().preferredPoolID,
					dropType: experienceStore().preferredDropType,
					fallbackDropType: "pin",
				});
			}

			// If still no active pool, user is not in a valid area for drops at this time.
			if (!this.activePool) {
				navigationStore().emitAction({
					type: ActionType.OPEN_POPUP,
					value: "out-of-drop-zone",
				});
				return false;
			}

			// Step 1: Pre-Drop Authorization with zoneCheck.
			// This verifies the user is inside a specific geofenced area (polygon or radius).
			if (this.activePool.dropType === "gps") {
				const lat = this.userLocation[1];
				const lng = this.userLocation[0];
				const zoneCheckResponse = await dropPoolStore().zoneCheck({
					poolID: this.activePool.id!,
					latitude: lat,
					longitude: lng,
					force: true,
				});

				if (!zoneCheckResponse?.data.allowed) {
					navigationStore().emitAction({
						type: ActionType.OPEN_POPUP,
						value: "out-of-drop-zone",
					});
					return false;
				}
			}

			// Step 2: Initiate the Drop with dropItems, but only if no items are currently loaded.
			if (this.vItems.length == 0) {
				// await vItemStore().recycleItems({
				// 	walletId: this.walletId!,
				// 	poolID: this.activePool.id!,
				// });

					await vItemStore().dropItems({
						walletId: authStore().getWalletID || "",
						segmentId: null,
						poolId: this.activePool.id!,
						userLocation: this.requiresGpsLocation
							? [this.userLocation[1], this.userLocation[0]]
							: undefined,
					});
				}
			return true;
		},

		selectItem(id: string) {
			console.log("Item ID /////////////////////////");
			console.log(id);
			this.selectedItemID = id;


			if (this.template.framework) {
				if (this.template.framework == "unity") {
					this.claimItem();
				} else {
					navigationStore().emitAction({
						type: ActionType.OPEN_POPUP,
						value: "claim-item",
					});
				}
			} else {
				navigationStore().emitAction({
					type: ActionType.OPEN_POPUP,
					value: "claim-item",
				});
			}
		},
		resolveClaimItemId(payload: unknown): string {
			if (typeof payload === "string") {
				return payload;
			}
			if (payload && typeof payload === "object") {
				const candidate = payload as {
					vitemId?: unknown;
					vItemId?: unknown;
					itemId?: unknown;
					id?: unknown;
					tokenId?: unknown;
				};
				for (const value of [
					candidate.vitemId,
					candidate.vItemId,
					candidate.itemId,
					candidate.id,
					candidate.tokenId,
				]) {
					if (typeof value === "string" && value.trim()) {
						return value;
					}
				}
			}
			return "";
		},
		async claimItem() {
			let id = "";
			if (this.selectedItemID.includes("webar-marker-")) {
				id = this.selectedItemID.split("/")[1];
			} else {
				id = this.selectedItemID;
			}
			if (!id) {
				throw new Error("Cannot claim item without a selected item id");
			}
			// check if item is pending
			const pendingItem = vItemStore().getPendingItems.find(
				(item) => item.id == id
			);
			try {
				await vItemStore().claimItem(id);
				this.sendMessage("itemClaimed", id);

				// check selected vitem skipToGameplay
				const vitem = vItemStore().items.find(
					(item) => item.id == id
				);

				this.showClaimModal = false;
				this.loading = false;
				if (pendingItem && pendingItem.isDeepLink) {
					vItemStore().selectItem(id);
					navigationStore().emitAction({
						type: ActionType.GO_TO_PAGE,
						value: "prize/" + id,
					});
					return;
				}
				// set as selected item if skipToGameplay is true
				// this will be used in the vault to skip to the prize page rendering
				if (vitem && vitem.skipToGameplay) {
					vItemStore().selectItem(id);
					navigationStore().emitAction({
						type: ActionType.GO_TO_PAGE,
						value: "prize/" + id,
					});
					return;
				}
			} catch (error) {
				console.error(error);
				this.showClaimModal = false;
			}
		},
		sendMessage(event: string, message: any) {
			this.iframeElement = document.getElementById(
				"ar-iframe"
			) as HTMLIFrameElement;
			const msg = JSON.stringify({
				event: event,
				message: message,
			});
			this.iframeElement!.contentWindow!.postMessage(msg, "*");
		},
		setupIframeListener() {
			this.iframeElement = document.getElementById(
				"ar-iframe"
			) as HTMLIFrameElement;

			this.iframeMessageHandler = async (e: MessageEvent) => {
				const data = JSON.parse(e.data);
				switch (data.event) {
					case "getAuth":
						const authObject = {
							campaignId: authStore().getCampaignID,
							userId: authStore().getUserID,
							walletId: authStore().getWalletID,
							refreshToken: localStorage.getItem("RefreshToken"),
							accessToken: localStorage.getItem("AccessToken"),
							billingSessionId: authStore().getSessionID,
							journeyId: authStore().getJourneyID,
						};
						this.sendMessage("auth", authObject);
						break;
					case "getDroppedItems":
						// If the store is empty (cleared on mount or after claim),
						// re-fetch from the backend so we never send stale data.
						if (this.vItems.length === 0 && this.activePool?.id) {
							await vItemStore().getItems();
						}
						this.sendMessage("droppedItems", this.vItems);
						break;
					case "claimItem":
						try {
							// const item = JSON.parse(data.message);
							this.selectItem(data.message);
						} catch (err) {
							console.error(err);
						}
						break;
					case "claim-item-directly":
						const claimItemId = this.resolveClaimItemId(data.message);
						if (!claimItemId) {
							console.error("claim-item-directly missing claim item id", data.message);
							return;
						}
						if (!window.claimedIds) window.claimedIds = new Set();
						if (!window.claimInFlightIds) window.claimInFlightIds = new Set();
						if (window.claimedIds.has(claimItemId) || window.claimInFlightIds.has(claimItemId)) {
							return;
						}
						try {
							window.claimInFlightIds.add(claimItemId);
							this.selectedItemID = claimItemId;
							await this.claimItem();
							window.claimedIds.add(claimItemId);

							// Remove claimed items from the store so that if
							// the iframe reloads (iOS back-nav) it cannot
							// receive the same items again.
							const remaining = vItemStore().items.filter(
								(item) => item.itemStatus === ItemStatus.DROPPED
							);
							vItemStore().items = remaining;
						} catch (err) {
							console.error(err);
						} finally {
							window.claimInFlightIds.delete(claimItemId);
						}
						break;
					case "alreadyClaimed":
						navigationStore().emitAction({
							type: ActionType.OPEN_POPUP,
							value: "already-claimed",
						});
						break;
					case "templateInit":
						// Initialize template configuration
						console.log("AR template initialized with config:", data.message);
						if (data.message && typeof data.message.hasAnimations === 'boolean') {
							this.templateHasAnimations = data.message.hasAnimations;
						}
						break;
					case "emitAction":
						navigationStore().emitAction(data.message);
						break;
				}
			};
			window.addEventListener("message", this.iframeMessageHandler);
		},
	},
});
</script>

<style scoped lang="scss">
#holder {
	width: 100%;
	height: 100%;
	background-color: #000;
}

#ar-iframe {
	width: 100%;
	height: 100%;
	border-style: none;
	background-color: #000;
}

a-scene {
	width: 100%;
	height: 100%;
}

.ar-loading {
	position: fixed;
	width: 100%;
	height: 100%;
	background-color: #000;
	z-index: 100;
}

.cross-hair {
	position: absolute;
	top: 50%;
	left: 50%;

	.hair {
		width: 200px;
		border-style: solid;
		border-width: 1px 0 0 0;
		border-color: #fff;
		position: absolute;
		transform: translate(-50%, -50%);
		opacity: 0.2;
	}

	.vertical {
		transform: rotate(90deg);
		left: -100px;
	}
}
</style>
